import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { usePageMeta } from '../lib/meta';
import type { LegalDocument } from '../legal/content';
import { Card, CardContent } from '../components/ui/card';
import { Alert } from '../components/ui/alert';

/** Renders a legal document: privacy policy or terms. */
export default function LegalPage({ document: doc }: { document: LegalDocument }) {
  const { t, locale } = useTranslation();
  const { pathname } = useLocation();

  // Public and indexable — legal pages are exactly the sort of thing a search
  // engine should be able to find.
  usePageMeta({
    title: t(doc.titleKey),
    description: doc.intro,
    canonicalPath: pathname,
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="text-2xl font-semibold tracking-tight text-fg">{t(doc.titleKey)}</h1>
      <p className="mt-2 text-sm text-muted">
        {t('legal.lastUpdated', {
          date: new Date(doc.lastUpdated).toLocaleDateString(locale, { dateStyle: 'long' }),
        })}
      </p>

      {locale !== 'en' && (
        <div className="mt-4">
          <Alert tone="info">{t('legal.authoritativeNote')}</Alert>
        </div>
      )}

      <p className="mt-6 text-sm leading-relaxed text-fg">{doc.intro}</p>

      <div className="mt-8 flex flex-col gap-6">
        {doc.sections.map((section) => (
          <Card key={section.heading}>
            <CardContent className="flex flex-col gap-3 pt-5">
              <h2 className="text-base font-semibold text-fg">{section.heading}</h2>

              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph} className="text-sm leading-relaxed text-muted">
                  {paragraph}
                </p>
              ))}

              {section.bullets && (
                <ul className="flex list-disc flex-col gap-2 ps-5 text-sm leading-relaxed text-muted">
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap gap-4 text-sm">
        <Link to="/contests" className="text-brand underline">
          {t('nav.contests')}
        </Link>
        <Link to="/privacy" className="text-brand underline">
          {t('legal.privacy')}
        </Link>
        <Link to="/terms" className="text-brand underline">
          {t('legal.terms')}
        </Link>
      </div>
    </div>
  );
}
